/* eslint-disable camelcase */
import { FragmentType, graphql, useFragment } from '@/generated/gql'
import { numberFormat } from '@/helpers/numbers'
import { Box, GridLegacy as Grid, Stack, Typography, useTheme } from '@mui/material'
import { PieChart } from '@mui/x-charts'

import React from 'react'
import { useTranslation } from 'react-i18next'

const canteenMenuItemNutritionalItemDataFragment = graphql(`
  fragment CanteenMenuItemNutritional_ItemDataFragmentV2 on MenuItem {
    _id
    macros_kcal
    macros_protein
    macros_carbs
    macros_sugar
    macros_fat
    macros_saturates
    macros_fibre
    macros_salt
  }
`)

type Props = {
  companyName: string
  itemData: FragmentType<typeof canteenMenuItemNutritionalItemDataFragment>
}

const MenuItemNutritional: React.FC<Props> = ({ companyName, itemData: itemDataProp }) => {
  const {
    macros_kcal,
    macros_protein,
    macros_carbs,
    macros_sugar,
    macros_fat,
    macros_saturates,
    macros_fibre,
    macros_salt,
  } = useFragment(canteenMenuItemNutritionalItemDataFragment, itemDataProp)

  const theme = useTheme()
  const { t } = useTranslation()

  const noData =
    !macros_kcal &&
    !macros_protein &&
    !macros_carbs &&
    !macros_sugar &&
    !macros_fat &&
    !macros_saturates &&
    !macros_fibre &&
    !macros_salt

  return (
    <Stack spacing={3} direction="column">
      <Stack direction="column">
        <Typography variant="subtitle3">
          {!noData ? t('cc:dialogTabs.nutritionalInfo') : t('cc:dialogTabs.noNutritionalInfo')}
        </Typography>
        {!noData ? (
          <Typography color="text.secondary" variant="body3">
            ({t('cc:allInformationProvidedBy')} {companyName})
          </Typography>
        ) : null}
      </Stack>
      {noData ? (
        <Typography variant="body2">
          {t('cc:dialogTabs.waitingForInfo', { companyName })}
        </Typography>
      ) : null}
      {!noData ? (
        <>
          <Box sx={{ width: '100%', maxWidth: 500 }}>
            <PieChart
              series={[
                {
                  data: [
                    {
                      id: 0,
                      value: macros_protein || 0,
                      label: `Protein`,
                      color: theme.palette.primary.main,
                    },
                    {
                      id: 1,
                      value: macros_carbs || 0,
                      label: `Carbs`,
                      color: theme.palette.info.main,
                    },
                    {
                      id: 2,
                      value: macros_fat || 0,
                      label: `Fat`,
                      color: theme.palette.warning.main,
                    },
                  ],
                },
              ]}
              height={200}
            />
          </Box>
          <Stack direction="row" spacing={0.5}>
            <Typography variant="subtitle3">{t('cc:dialogTabs.baseDish')}</Typography>
            <Typography color="text.secondary">{t('cc:dialogTabs.servesOne')}</Typography>
          </Stack>
          <Grid container spacing={1}>
            {[
              {
                value: macros_kcal,
                unit: 'kcal',
                label: 'energy',
                decimalPlaces: 0,
              },
              {
                value: macros_protein,
                unit: 'g',
                label: 'protein',
                decimalPlaces: 0,
              },
              {
                value: macros_carbs,
                unit: 'g',
                label: 'carbs',
                decimalPlaces: 0,
              },
              {
                value: macros_sugar,
                unit: 'g',
                label: 'sugars',
                decimalPlaces: 0,
              },
              {
                value: macros_fat,
                unit: 'g',
                label: 'fat',
                decimalPlaces: 0,
              },
              {
                value: macros_saturates,
                unit: 'g',
                label: 'sat. fat',
                decimalPlaces: 0,
              },
              {
                value: macros_fibre,
                unit: 'g',
                label: 'fibre',
                decimalPlaces: 0,
              },
              {
                value: macros_salt,
                unit: 'mg',
                label: 'salt',
                decimalPlaces: 0,
              },
            ].map((item) => (
              <Grid key={`${item.value}-${item.label}`} item xs={4} sm={3} md={2}>
                <Box
                  sx={{
                    borderRadius: '6px',
                    paddingY: 2,
                    paddingX: 1,
                    backgroundColor: 'background.default',
                  }}>
                  <Typography textAlign="center">
                    {numberFormat(item.value || 0, item.decimalPlaces)}
                    {item.unit}
                  </Typography>
                  <Typography textAlign="center">{item.label}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </>
      ) : null}
    </Stack>
  )
}

export default MenuItemNutritional
